/**
 * BoardDetail — Trello-style kanban for a single board.
 *
 * Features:
 *   • Lists rendered horizontally; cards stack vertically.
 *   • Add list / Add card (inline + modal).
 *   • Click a card → CardModal: edit title, description, due date, members,
 *     checklist, attachments (PDF + image), comments, activity feed.
 *   • Admin/Supervisor (or personal-board owner) can reassign cards.
 *   • HTML5 drag-and-drop between lists.
 *   • Permission gates mirror the backend exactly.
 *
 * Permissions UI (matches routes/boards.py):
 *   - admin/supervisor on company boards: full.
 *   - owner on personal boards: full.
 *   - staff on company boards: comment + checklist + attach + create cards;
 *     can edit/move/drag cards they're a member of; CANNOT reassign cards
 *     they didn't create unless they're already a member of it.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Plus, X, ArrowLeft, MoreHorizontal, Trash2, Archive,
  Calendar, Users as UsersIcon, MessageSquare, Paperclip, CheckSquare, Lock, Building, Send, Tag, Sparkles,
  FileText, Download, Image as ImageIcon, Share2, Palette, Eye, Check,
  Search, Filter, Pencil, FileSpreadsheet, FileArchive, ShieldAlert, Clock,
} from 'lucide-react';
import Lightbox from '../components/Lightbox';

const API = `${window.__BACKEND_URL__}/api`;

// File-type icon + color resolver for board attachments. Matches by file
// extension (case-insensitive) — backend stores `original_name` so this works
// even when the browser sends application/octet-stream for Office docs.
const FILE_TYPE_META = {
  pdf:  { color: '#EF4444', label: 'PDF',   icon: 'pdf'   },
  doc:  { color: '#1E88E5', label: 'Word',  icon: 'doc'   },
  docx: { color: '#1E88E5', label: 'Word',  icon: 'doc'   },
  xls:  { color: '#10B981', label: 'Excel', icon: 'sheet' },
  xlsx: { color: '#10B981', label: 'Excel', icon: 'sheet' },
  csv:  { color: '#10B981', label: 'CSV',   icon: 'sheet' },
  ppt:  { color: '#F97316', label: 'PowerPoint', icon: 'doc' },
  pptx: { color: '#F97316', label: 'PowerPoint', icon: 'doc' },
  odt:  { color: '#1E88E5', label: 'ODT',   icon: 'doc'   },
  ods:  { color: '#10B981', label: 'ODS',   icon: 'sheet' },
  odp:  { color: '#F97316', label: 'ODP',   icon: 'doc'   },
  txt:  { color: '#64748B', label: 'TXT',   icon: 'doc'   },
  zip:  { color: '#A78BFA', label: 'ZIP',   icon: 'zip', archive: true },
};

const attachmentMeta = (a) => {
  const name = a?.original_name || a?.filename || '';
  const ext = (name.split('.').pop() || '').toLowerCase();
  return FILE_TYPE_META[ext] || { color: '#94A3B8', label: 'File', icon: 'doc' };
};

const fmtFileSize = (bytes) => {
  if (!bytes) return '0 KB';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Format an ISO/string timestamp as a short human-readable label
// ("Jun 1, 2026 · 9:02 AM") for board card comments, activity, and
// attachment timelines. Returns '' for falsy inputs so we can render
// nothing instead of "Invalid Date".
const fmtTimestamp = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const datePart = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const timePart = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${datePart} · ${timePart}`;
};

// Full ISO-ish tooltip for hovering ("Monday, June 1, 2026 at 9:02:14 AM").
const fmtTimestampFull = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit',
  });
};

// Compact relative-time label for the card-preview "Last activity" badge.
// < 1 min   → "just now"
// < 60 min  → "5m ago"
// < 24 hrs  → "3h ago"
// < 7 days  → "2d ago"
// otherwise → short date like "Mar 15".
const fmtRelative = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// Resolve the most-recent activity timestamp for a card by looking at the
// last comment, last activity entry, last attachment upload, and falling
// back to the card's own created_at. Used by the kanban-preview "Last
// activity" badge so users can spot fresh cards at a glance.
const lastActivityOf = (card) => {
  if (!card) return null;
  const candidates = [];
  const lastIso = (arr, key) => {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    // Arrays are append-only chronologically, but be defensive and grab max.
    let max = null;
    for (const item of arr) {
      const v = item && item[key];
      if (v && (!max || v > max)) max = v;
    }
    return max;
  };
  const c1 = lastIso(card.comments, 'created_at');
  const c2 = lastIso(card.activity, 'created_at');
  const c3 = lastIso(card.attachments, 'uploaded_at');
  [c1, c2, c3, card.created_at].forEach((v) => { if (v) candidates.push(v); });
  if (candidates.length === 0) return null;
  return candidates.reduce((a, b) => (a > b ? a : b));
};

// Card-color palette — used for the top stripe on a card + a small dot
// next to the title in the list view. The user wanted to color-code cards
// by company (e.g. "Acme = blue") for quick visual scanning.
// Expanded to 20 presets covering every common workflow color, plus a
// "Custom…" entry that pops a native color input for anything else.
const CARD_COLORS = [
  { name: 'No color',   color: null },
  { name: 'Red',        color: '#EF4444' },
  { name: 'Crimson',    color: '#DC2626' },
  { name: 'Orange',     color: '#F97316' },
  { name: 'Amber',      color: '#FFA000' },
  { name: 'Yellow',     color: '#FACC15' },
  { name: 'Lime',       color: '#84CC16' },
  { name: 'Green',      color: '#00C896' },
  { name: 'Emerald',    color: '#10B981' },
  { name: 'Teal',       color: '#14B8A6' },
  { name: 'Cyan',       color: '#06B6D4' },
  { name: 'Sky',        color: '#0EA5E9' },
  { name: 'Blue',       color: '#1E88E5' },
  { name: 'Indigo',     color: '#6366F1' },
  { name: 'Purple',     color: '#A78BFA' },
  { name: 'Violet',     color: '#8B5CF6' },
  { name: 'Pink',       color: '#EC4899' },
  { name: 'Rose',       color: '#F43F5E' },
  { name: 'Brown',      color: '#A16207' },
  { name: 'Slate',      color: '#64748B' },
  { name: 'Charcoal',   color: '#334155' },
];

export default function BoardDetail() {
  const { boardId } = useParams();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const [board, setBoard] = useState(null);
  const [lists, setLists] = useState([]);
  const [cards, setCards] = useState([]);
  const [users, setUsers] = useState([]);
  const [openCard, setOpenCard] = useState(null);
  // Label filter — set of label-ids the user has pinned in the filter bar.
  // Card must carry ALL selected labels to show (AND-filter). Empty set = show all.
  const [labelFilter, setLabelFilter] = useState(new Set());
  // Card title search — case-insensitive substring on title + description.
  const [cardSearch, setCardSearch] = useState('');
  const [addingListMode, setAddingListMode] = useState(false);
  const [draftListName, setDraftListName] = useState('');
  const [addingCardListId, setAddingCardListId] = useState(null);
  const [draftCardTitle, setDraftCardTitle] = useState('');
  const [dragCardId, setDragCardId] = useState(null);
  // Lightbox state: when set, renders <Lightbox/> overlay
  const [lightbox, setLightbox] = useState(null); // { images: [{src, name}], index: number }

  const isPriv = user?.role === 'admin' || user?.role === 'supervisor';
  const isOwnerOfPersonal = board?.visibility === 'personal' && board?.owner_id === user?.id;
  const canAdminBoard = isOwnerOfPersonal || (board?.visibility === 'company' && isPriv);

  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const [b, u] = await Promise.all([
        axios.get(`${API}/boards/${boardId}`, auth),
        axios.get(`${API}/users`, auth).catch(() => ({ data: [] })),
      ]);
      setBoard(b.data.board);
      setLists(b.data.lists);
      setCards(b.data.cards);
      setUsers(u.data || []);
    } catch (err) {
      console.error('load board failed', err);
      if (err.response?.status === 404 || err.response?.status === 403) {
        navigate('/boards');
      }
    }
  }, [boardId, auth, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-open a card if ?card= present (deep links from search)
  useEffect(() => {
    const id = search.get('card');
    if (!id || !cards.length) return;
    const c = cards.find((x) => x.id === id);
    if (c) setOpenCard(c);
  }, [search, cards]);

  // ─── Helpers
  const refresh = fetchAll;
  const userById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  // Build a fully-qualified URL for an attachment, including the auth token
  // as a query param (browsers don't send Authorization for <img src>).
  const attachmentHref = useCallback(
    (a) => `${window.__BACKEND_URL__}${a.url}?token=${token}`,
    [token]
  );

  // Pre-S3 attachments don't have `s3_key`. On production those files were
  // wiped on every redeploy because the filesystem is ephemeral — they're
  // dead and can't be recovered. We surface a clear "re-upload needed"
  // badge instead of a broken image / dead download link.
  const isLegacyOrphan = (a) => !a.s3_key;

  const isImageAttachment = (a) => (a.content_type || '').startsWith('image/');

  // Build a small list of {src, name} image refs for the lightbox out of an
  // arbitrary attachments array.
  const imageRefsFrom = useCallback(
    (attachments) =>
      (attachments || [])
        .filter(isImageAttachment)
        .map((a) => ({ src: attachmentHref(a), name: a.original_name || 'image', id: a.id })),
    [attachmentHref]
  );

  const openLightbox = useCallback((images, index = 0) => {
    if (!images || images.length === 0) return;
    setLightbox({ images, index: Math.max(0, Math.min(index, images.length - 1)) });
  }, []);

  const addList = async () => {
    if (!draftListName.trim()) return;
    try {
      await axios.post(`${API}/boards/${boardId}/lists`, { name: draftListName.trim() }, auth);
      setDraftListName(''); setAddingListMode(false);
      refresh();
    } catch (err) { console.error(err); alert(err.response?.data?.detail || 'Failed to add list'); }
  };

  const addCard = async (listId) => {
    if (!draftCardTitle.trim()) return;
    try {
      await axios.post(`${API}/boards/${boardId}/cards`, { list_id: listId, title: draftCardTitle.trim() }, auth);
      setDraftCardTitle(''); setAddingCardListId(null);
      refresh();
    } catch (err) { console.error(err); alert(err.response?.data?.detail || 'Failed to add card'); }
  };

  const deleteList = async (listId) => {
    if (!window.confirm('Delete this list and all its cards?')) return;
    try {
      await axios.delete(`${API}/boards/${boardId}/lists/${listId}`, auth);
      refresh();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const onDrop = async (toListId) => {
    if (!dragCardId) return;
    const card = cards.find((c) => c.id === dragCardId);
    setDragCardId(null);
    if (!card || card.list_id === toListId) return;
    // Optimistic UI
    setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, list_id: toListId } : c)));
    try {
      await axios.patch(`${API}/boards/${boardId}/cards/${card.id}`, { list_id: toListId }, auth);
      refresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to move card');
      refresh();
    }
  };

  // Group cards by list (sorted by position). MUST be declared before any
  // conditional return so the hook order is stable across renders.
  const cardsByList = useMemo(() => {
    const m = {};
    const q = cardSearch.trim().toLowerCase();
    for (const c of cards) {
      // Apply label AND-filter
      if (labelFilter.size > 0) {
        const have = new Set((c.labels || []).map((l) => l.id).filter(Boolean));
        let ok = true;
        for (const need of labelFilter) {
          if (!have.has(need)) { ok = false; break; }
        }
        if (!ok) continue;
      }
      // Apply title/desc search
      if (q && !`${c.title || ''}\n${c.description || ''}`.toLowerCase().includes(q)) continue;
      (m[c.list_id] = m[c.list_id] || []).push(c);
    }
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.position - b.position));
    return m;
  }, [cards, labelFilter, cardSearch]);

  const filterActive = labelFilter.size > 0 || cardSearch.trim().length > 0;

  if (!board) return <div className="p-8 text-center" style={{ color: '#A0AEC0' }}>Loading board…</div>;

  return (
    <div className="min-h-full" data-testid="board-detail">
      {/* Header */}
      <div className="px-4 md:px-6 pt-4 pb-3 flex items-center justify-between gap-2 sticky top-0 z-20" style={{ background: 'rgba(11,19,33,0.7)', backdropFilter: 'blur(8px)' }}>
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => navigate('/boards')} className="p-2 rounded-lg hover:bg-white/5" data-testid="back-to-boards">
            <ArrowLeft className="w-4 h-4 text-white" />
          </button>
          <div className="w-1.5 h-7 rounded" style={{ background: board.color }} />
          <h1 className="text-lg font-bold text-white truncate">{board.name}</h1>
          <span
            className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide flex items-center gap-1"
            style={{
              background: board.visibility === 'personal' ? 'rgba(167,139,250,0.15)' : 'rgba(30,136,229,0.15)',
              color: board.visibility === 'personal' ? '#A78BFA' : '#1E88E5',
            }}
          >
            {board.visibility === 'personal' ? <Lock className="w-3 h-3" /> : <Building className="w-3 h-3" />}
            {board.visibility}
          </span>
        </div>
        {canAdminBoard && (
          <button
            onClick={async () => {
              if (!window.confirm('Delete this board and ALL its lists and cards? This cannot be undone.')) return;
              try {
                await axios.delete(`${API}/boards/${boardId}`, auth);
                navigate('/boards');
              } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
            }}
            className="flex items-center gap-1 px-2 py-1 rounded text-xs"
            style={{ color: '#EF4444' }}
            data-testid="delete-board-btn"
            title="Delete board"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        )}
      </div>

      {/* Filter bar — label filter + card title search */}
      {(board.labels?.length > 0 || cards.length > 0) && (
        <div
          className="px-4 md:px-6 pb-3 flex items-center gap-2 flex-wrap"
          data-testid="board-filter-bar"
        >
          {/* Search box */}
          <div
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg flex-1 max-w-xs"
            style={{ background: '#0F1923', border: '1px solid #2D3748' }}
          >
            <Search className="w-3.5 h-3.5" style={{ color: '#64748B' }} />
            <input
              type="text"
              placeholder="Search cards…"
              value={cardSearch}
              onChange={(e) => setCardSearch(e.target.value)}
              className="bg-transparent text-xs text-white outline-none flex-1 min-w-0"
              data-testid="board-card-search"
            />
            {cardSearch && (
              <button
                onClick={() => setCardSearch('')}
                className="text-[10px]"
                style={{ color: '#64748B' }}
                data-testid="board-card-search-clear"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Label filter pills */}
          {(board.labels || []).length > 0 && (
            <>
              <Filter className="w-3.5 h-3.5 ml-1" style={{ color: '#64748B' }} />
              <div className="flex items-center gap-1 flex-wrap" data-testid="board-label-filter">
                {(board.labels || []).map((lab) => {
                  const on = labelFilter.has(lab.id);
                  return (
                    <button
                      key={lab.id}
                      onClick={() => {
                        const next = new Set(labelFilter);
                        on ? next.delete(lab.id) : next.add(lab.id);
                        setLabelFilter(next);
                      }}
                      className="text-[10px] font-semibold px-2 py-0.5 rounded-full text-white transition-all"
                      style={{
                        background: on ? lab.color : 'transparent',
                        border: `1px solid ${lab.color}`,
                        opacity: on ? 1 : 0.7,
                      }}
                      data-testid={`board-label-filter-${lab.id}`}
                      title={on ? `Hide ${lab.name}` : `Show only ${lab.name}`}
                    >
                      {lab.name}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {filterActive && (
            <button
              onClick={() => { setLabelFilter(new Set()); setCardSearch(''); }}
              className="text-[10px] px-2 py-0.5 rounded-full ml-auto"
              style={{ background: 'rgba(239,68,68,0.12)', color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.3)' }}
              data-testid="board-filter-clear"
            >
              Clear filter
            </button>
          )}
        </div>
      )}

      {/* Kanban */}
      <div className="px-4 md:px-6 pb-6 flex gap-3 overflow-x-auto" style={{ minHeight: 'calc(100vh - 180px)' }} data-testid="board-lists">
        {lists.map((list) => (
          <div
            key={list.id}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(list.id)}
            className="w-72 flex-shrink-0 rounded-xl p-2"
            style={{ background: '#0F1923', border: '1px solid #2D3748' }}
            data-testid={`list-${list.id}`}
          >
            <div className="flex items-center justify-between px-2 py-1.5">
              <span className="text-sm font-semibold text-white flex items-center gap-2">
                {list.color && <span className="w-2 h-2 rounded-full" style={{ background: list.color }} />}
                {list.name}
                <span className="text-xs" style={{ color: '#64748B' }}>{(cardsByList[list.id] || []).length}</span>
              </span>
              {canAdminBoard && (
                <button onClick={() => deleteList(list.id)} className="p-1 rounded hover:bg-white/5" title="Delete list" data-testid={`delete-list-${list.id}`}>
                  <Trash2 className="w-3.5 h-3.5" style={{ color: '#EF4444' }} />
                </button>
              )}
            </div>

            <div className="space-y-2 max-h-[70vh] overflow-y-auto px-1 pb-1">
              {(cardsByList[list.id] || []).map((card) => (
                <button
                  key={card.id}
                  draggable
                  onDragStart={() => setDragCardId(card.id)}
                  onClick={() => setOpenCard(card)}
                  className="w-full text-left rounded-lg hover:border-blue-500/40 transition-all overflow-hidden"
                  style={{ background: '#1A2433', border: '1px solid #2D3748' }}
                  data-testid={`card-${card.id}`}
                >
                  {/* Color stripe (top edge) — 3px thick for clear visibility */}
                  {card.color && (
                    <div
                      className="h-3 w-full"
                      style={{ background: card.color }}
                      data-testid={`card-color-stripe-${card.id}`}
                    />
                  )}
                  <div className="p-3">
                  {card.labels?.length > 0 && (
                    <div className="flex gap-1 mb-1.5 flex-wrap" data-testid={`card-labels-${card.id}`}>
                      {card.labels.map((lab, i) => (
                        <span
                          key={lab.id || i}
                          className="text-[10px] font-semibold px-2 py-0.5 rounded-full text-white max-w-[160px] truncate"
                          style={{ background: lab.color || '#64748B' }}
                          title={lab.name || ''}
                        >
                          {lab.name || ''}
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-sm text-white flex items-start gap-2">
                    {card.color && (
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5"
                        style={{ background: card.color }}
                        data-testid={`card-color-dot-${card.id}`}
                      />
                    )}
                    <span className="flex-1">{card.title}</span>
                  </p>
                  {(() => {
                    const imgs = imageRefsFrom(card.attachments);
                    if (imgs.length === 0) return null;
                    return (
                      <div
                        className="mt-2 flex gap-1.5 flex-wrap"
                        data-testid={`card-thumbs-${card.id}`}
                      >
                        {imgs.slice(0, 4).map((img, idx) => (
                          <span
                            key={img.id}
                            role="button"
                            tabIndex={-1}
                            aria-label={`Preview ${img.name}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              openLightbox(imgs, idx);
                            }}
                            className="relative block rounded overflow-hidden flex-shrink-0 cursor-zoom-in"
                            style={{ width: 64, height: 64, border: '1px solid #2D3748', background: '#0F1923' }}
                            data-testid={`card-thumb-${card.id}-${idx}`}
                          >
                            <img src={img.src} alt={img.name} className="w-full h-full object-cover" loading="lazy" />
                            {idx === 3 && imgs.length > 4 && (
                              <span className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-xs font-semibold">
                                +{imgs.length - 4}
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                  <div className="mt-1.5 flex items-center gap-2 text-xs" style={{ color: '#64748B' }}>
                    {card.due_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {card.due_date}</span>}
                    {card.comments?.length > 0 && <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {card.comments.length}</span>}
                    {card.attachments?.length > 0 && <span className="flex items-center gap-1"><Paperclip className="w-3 h-3" /> {card.attachments.length}</span>}
                    {card.checklist?.length > 0 && (
                      <span className="flex items-center gap-1">
                        <CheckSquare className="w-3 h-3" />
                        {card.checklist.filter((i) => i.done).length}/{card.checklist.length}
                      </span>
                    )}
                  </div>
                  {(() => {
                    const last = lastActivityOf(card);
                    if (!last) return null;
                    return (
                      <div
                        className="mt-1.5 flex items-center gap-1 text-[10px]"
                        style={{ color: '#64748B' }}
                        title={`Last activity: ${fmtTimestampFull(last)}`}
                        data-testid={`card-last-activity-${card.id}`}
                      >
                        <Clock className="w-2.5 h-2.5" />
                        <span>Last activity: {fmtRelative(last)}</span>
                      </div>
                    );
                  })()}
                  {card.member_ids?.length > 0 && (
                    <div className="mt-2 flex -space-x-1">
                      {card.member_ids.slice(0, 4).map((mid) => {
                        const m = userById[mid];
                        const initials = (m?.name || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
                        return (
                          <span key={mid} className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white" style={{ background: '#2D3748', border: '1px solid #1A2433' }} title={m?.name || mid}>
                            {initials}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  </div>{/* /p-3 inner */}
                </button>
              ))}

              {addingCardListId === list.id ? (
                <div className="p-2 rounded-lg" style={{ background: '#1A2433', border: '1px solid #2D3748' }}>
                  <textarea
                    autoFocus
                    value={draftCardTitle}
                    onChange={(e) => setDraftCardTitle(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addCard(list.id); } }}
                    placeholder="Card title…"
                    rows={2}
                    className="w-full p-2 rounded text-sm text-white resize-none"
                    style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                    data-testid={`new-card-input-${list.id}`}
                  />
                  <div className="mt-1 flex gap-1">
                    <button onClick={() => addCard(list.id)} className="px-3 py-1 rounded text-xs font-semibold text-white" style={{ background: '#1E88E5' }} data-testid={`add-card-confirm-${list.id}`}>
                      Add Card
                    </button>
                    <button onClick={() => { setAddingCardListId(null); setDraftCardTitle(''); }} className="p-1 rounded text-xs" style={{ color: '#64748B' }}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => { setAddingCardListId(list.id); setDraftCardTitle(''); }}
                  className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium hover:bg-white/5 transition"
                  style={{ color: '#A0AEC0' }}
                  data-testid={`add-card-${list.id}`}
                >
                  <Plus className="w-3.5 h-3.5" /> Add a card
                </button>
              )}
            </div>
          </div>
        ))}

        {canAdminBoard && (
          <div className="w-72 flex-shrink-0">
            {addingListMode ? (
              <div className="rounded-xl p-2" style={{ background: '#0F1923', border: '1px solid #2D3748' }}>
                <input
                  autoFocus
                  value={draftListName}
                  onChange={(e) => setDraftListName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addList(); }}
                  placeholder="List name…"
                  className="w-full p-2 rounded text-sm text-white"
                  style={{ background: '#1A2433', border: '1px solid #2D3748' }}
                  data-testid="new-list-input"
                />
                <div className="mt-2 flex gap-1">
                  <button onClick={addList} className="px-3 py-1.5 rounded text-xs font-semibold text-white" style={{ background: '#1E88E5' }} data-testid="add-list-confirm">
                    Add List
                  </button>
                  <button onClick={() => { setAddingListMode(false); setDraftListName(''); }} className="p-1 rounded" style={{ color: '#64748B' }}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setAddingListMode(true)}
                className="w-full p-3 rounded-xl flex items-center justify-center gap-1.5 text-sm font-medium"
                style={{ background: 'rgba(255,255,255,0.04)', color: '#A0AEC0', border: '1px dashed #2D3748' }}
                data-testid="add-list-btn"
              >
                <Plus className="w-4 h-4" /> Add another list
              </button>
            )}
          </div>
        )}
      </div>

      {openCard && (
        <CardModal
          card={openCard}
          board={board}
          users={users}
          lists={lists}
          canAdminBoard={canAdminBoard}
          isPriv={isPriv}
          currentUserId={user?.id}
          auth={auth}
          boardId={boardId}
          attachmentHref={attachmentHref}
          isImageAttachment={isImageAttachment}
          isLegacyOrphan={isLegacyOrphan}
          imageRefsFrom={imageRefsFrom}
          openLightbox={openLightbox}
          onClose={() => { setOpenCard(null); if (search.get('card')) setSearch({}); }}
          onChange={refresh}
        />
      )}

      {lightbox && (
        <Lightbox
          images={lightbox.images}
          index={lightbox.index}
          onIndexChange={(i) => setLightbox((lb) => (lb ? { ...lb, index: i } : lb))}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

/* ─────────────────────────── Card modal ─────────────────────────── */
function BoardLabelsPicker({ card, board, auth, boardId, canEdit, canAdminBoard, onCardLabelsChange, onBoardLabelsChange }) {
  // ─── State ───────────────────────────────────────────────────────────
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [draftColor, setDraftColor] = useState('#1E88E5');
  const [busy, setBusy] = useState(false);
  // Local mirror of the board's label registry so edits feel instant while
  // the parent refresh races to refetch.
  const [boardLabels, setBoardLabels] = useState(board?.labels || []);
  useEffect(() => { setBoardLabels(board?.labels || []); }, [board?.labels]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return boardLabels;
    return boardLabels.filter((lb) => (lb.name || '').toLowerCase().includes(q));
  }, [boardLabels, query]);

  const appliedIds = new Set((card.labels || []).map((l) => l.id).filter(Boolean));

  // ─── Helpers ─────────────────────────────────────────────────────────
  const applyToggle = async (lab) => {
    if (!canEdit) return;
    const on = appliedIds.has(lab.id);
    const next = on
      ? (card.labels || []).filter((l) => l.id !== lab.id)
      : [...(card.labels || []), { id: lab.id, name: lab.name, color: lab.color }];
    onCardLabelsChange(next);
  };

  const startCreate = () => {
    setCreating(true);
    setEditingId(null);
    setDraftName('');
    setDraftColor('#1E88E5');
  };

  const startEdit = (lab) => {
    setEditingId(lab.id);
    setCreating(false);
    setDraftName(lab.name);
    setDraftColor(lab.color);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setCreating(false);
    setDraftName('');
    setDraftColor('#1E88E5');
  };

  const submitCreate = async () => {
    const name = draftName.trim();
    if (!name) return;
    if (!/^#[0-9A-Fa-f]{6}$/.test(draftColor)) { alert('Pick a valid color.'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/boards/${boardId}/labels`, { name, color: draftColor }, auth);
      setBoardLabels((prev) => [...prev, r.data]);
      cancelEdit();
      onBoardLabelsChange();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to create label');
    } finally { setBusy(false); }
  };

  const submitEdit = async () => {
    const name = draftName.trim();
    if (!name) return;
    if (!/^#[0-9A-Fa-f]{6}$/.test(draftColor)) { alert('Pick a valid color.'); return; }
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/boards/${boardId}/labels/${editingId}`, { name, color: draftColor }, auth);
      setBoardLabels((prev) => prev.map((lb) => (lb.id === editingId ? r.data : lb)));
      cancelEdit();
      onBoardLabelsChange();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update label');
    } finally { setBusy(false); }
  };

  const deleteLabel = async (lab) => {
    if (!window.confirm(`Delete label "${lab.name}"? It will be removed from every card.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/boards/${boardId}/labels/${lab.id}`, auth);
      setBoardLabels((prev) => prev.filter((lb) => lb.id !== lab.id));
      onBoardLabelsChange();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete label');
    } finally { setBusy(false); }
  };

  // ─── Render ──────────────────────────────────────────────────────────
  return (
    <details
      open
      className="rounded-lg"
      style={{ background: '#0F1923', border: '1px solid #2D3748' }}
    >
      <summary className="px-3 py-2 text-sm text-white flex items-center gap-2 cursor-pointer">
        <Tag className="w-4 h-4" /> Labels
        <span className="ml-auto text-[10px]" style={{ color: '#64748B' }}>
          {appliedIds.size > 0 ? `${appliedIds.size} applied` : ''}
        </span>
      </summary>
      <div className="px-2 pb-2 space-y-2" data-testid="card-labels-picker">
        {/* Search */}
        <div
          className="flex items-center gap-1.5 px-2 py-1 rounded"
          style={{ background: '#1A2433', border: '1px solid #2D3748' }}
        >
          <Search className="w-3 h-3" style={{ color: '#64748B' }} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search labels…"
            className="flex-1 bg-transparent text-[11px] text-white outline-none min-w-0"
            data-testid="label-search-input"
          />
        </div>

        {/* Label list */}
        <ul className="space-y-1 max-h-48 overflow-y-auto">
          {filtered.length === 0 && (
            <li className="text-[11px] text-center py-2" style={{ color: '#64748B' }}>
              {query ? 'No matches' : 'No labels yet — create one below'}
            </li>
          )}
          {filtered.map((lab) => {
            const on = appliedIds.has(lab.id);
            const isEditing = editingId === lab.id;
            if (isEditing) {
              return (
                <li
                  key={lab.id}
                  className="rounded p-2 space-y-1.5"
                  style={{ background: '#1A2433' }}
                  data-testid={`label-edit-${lab.id}`}
                >
                  <input
                    type="text"
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    className="w-full px-2 py-1 rounded text-[11px] text-white outline-none"
                    style={{ background: '#0B1321', border: '1px solid #2D3748' }}
                    placeholder="Label name"
                    autoFocus
                  />
                  <div className="flex items-center gap-1.5">
                    <input
                      type="color"
                      value={draftColor}
                      onChange={(e) => setDraftColor(e.target.value)}
                      className="w-7 h-7 rounded cursor-pointer bg-transparent"
                      style={{ border: '1px solid #2D3748' }}
                    />
                    <input
                      type="text"
                      value={draftColor}
                      onChange={(e) => setDraftColor(e.target.value)}
                      className="flex-1 px-2 py-1 rounded text-[10px] text-white font-mono"
                      style={{ background: '#0B1321', border: '1px solid #2D3748' }}
                    />
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={submitEdit}
                      disabled={busy}
                      className="flex-1 px-2 py-1 rounded text-[10px] font-semibold text-white"
                      style={{ background: '#1E88E5' }}
                      data-testid={`label-save-${lab.id}`}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => deleteLabel(lab)}
                      disabled={busy}
                      className="px-2 py-1 rounded text-[10px]"
                      style={{ background: 'rgba(239,68,68,0.15)', color: '#FCA5A5' }}
                      data-testid={`label-delete-${lab.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="px-2 py-1 rounded text-[10px]"
                      style={{ color: '#94A3B8' }}
                    >
                      Cancel
                    </button>
                  </div>
                </li>
              );
            }
            return (
              <li
                key={lab.id}
                className="flex items-center gap-1.5"
                data-testid={`label-row-${lab.id}`}
              >
                <button
                  onClick={() => applyToggle(lab)}
                  disabled={!canEdit}
                  className="flex-1 flex items-center gap-2 px-2 py-1.5 rounded text-[11px] hover:bg-white/5 disabled:opacity-60"
                  style={{
                    background: lab.color,
                    color: '#fff',
                    fontWeight: 600,
                    border: on ? '2px solid #fff' : '2px solid transparent',
                  }}
                  data-testid={`label-toggle-${lab.id}`}
                  title={on ? 'Remove from card' : 'Add to card'}
                >
                  <span className="flex-1 truncate text-left">{lab.name}</span>
                  {on && <Check className="w-3.5 h-3.5" />}
                </button>
                {canAdminBoard && (
                  <button
                    onClick={() => startEdit(lab)}
                    className="p-1.5 rounded hover:bg-white/5"
                    style={{ color: '#94A3B8' }}
                    data-testid={`label-edit-btn-${lab.id}`}
                    title="Edit label"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>

        {/* Create new */}
        {canAdminBoard && (
          creating ? (
            <div
              className="rounded p-2 space-y-1.5"
              style={{ background: '#1A2433', border: '1px solid #2D3748' }}
              data-testid="label-create-form"
            >
              <input
                type="text"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                placeholder="New label name"
                autoFocus
                className="w-full px-2 py-1 rounded text-[11px] text-white outline-none"
                style={{ background: '#0B1321', border: '1px solid #2D3748' }}
                data-testid="label-create-name"
              />
              <div className="flex items-center gap-1.5">
                <input
                  type="color"
                  value={draftColor}
                  onChange={(e) => setDraftColor(e.target.value)}
                  className="w-7 h-7 rounded cursor-pointer bg-transparent"
                  style={{ border: '1px solid #2D3748' }}
                  data-testid="label-create-color"
                />
                <input
                  type="text"
                  value={draftColor}
                  onChange={(e) => setDraftColor(e.target.value)}
                  className="flex-1 px-2 py-1 rounded text-[10px] text-white font-mono"
                  style={{ background: '#0B1321', border: '1px solid #2D3748' }}
                  data-testid="label-create-hex"
                />
              </div>
              <div className="flex gap-1">
                <button
                  onClick={submitCreate}
                  disabled={busy || !draftName.trim()}
                  className="flex-1 px-2 py-1 rounded text-[10px] font-semibold text-white disabled:opacity-60"
                  style={{ background: '#10B981' }}
                  data-testid="label-create-confirm"
                >
                  Create
                </button>
                <button
                  onClick={cancelEdit}
                  className="px-2 py-1 rounded text-[10px]"
                  style={{ color: '#94A3B8' }}
                  data-testid="label-create-cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={startCreate}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded text-[11px]"
              style={{ background: '#1A2433', border: '1px dashed #2D3748', color: '#94A3B8' }}
              data-testid="label-create-btn"
            >
              <Plus className="w-3 h-3" /> Create a new label
            </button>
          )
        )}
      </div>
    </details>
  );
}

function CardModal({ card, board, users, lists, canAdminBoard, isPriv, currentUserId, auth, boardId, attachmentHref, isImageAttachment, isLegacyOrphan, imageRefsFrom, openLightbox, onClose, onChange }) {
  const [c, setC] = useState(card);
  const [newComment, setNewComment] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  const [savingDesc, setSavingDesc] = useState(false);
  const [showLabelPicker, setShowLabelPicker] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareRole, setShareRole] = useState('editor');
  const [shareSelected, setShareSelected] = useState([]);
  const [shareMessage, setShareMessage] = useState('');
  const [sharing, setSharing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summarizing, setSummarizing] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  useEffect(() => { setC(card); }, [card]);

  const isMember = (c.member_ids || []).includes(currentUserId);
  const canEdit = canAdminBoard || isMember;

  const patch = async (update) => {
    const r = await axios.patch(`${API}/boards/${boardId}/cards/${c.id}`, update, auth);
    setC(r.data);
    onChange();
  };

  const saveTitle = async () => {
    if (!c.title.trim() || c.title === card.title) return;
    setSavingTitle(true);
    try { await patch({ title: c.title }); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setSavingTitle(false); }
  };

  const saveDesc = async () => {
    if (c.description === card.description) return;
    setSavingDesc(true);
    try { await patch({ description: c.description }); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setSavingDesc(false); }
  };

  const toggleMember = async (uid) => {
    if (!isPriv && !(board.visibility === 'personal' && board.owner_id === currentUserId)) {
      alert('Only admin/supervisor can reassign cards');
      return;
    }
    const ids = c.member_ids || [];
    const next = ids.includes(uid) ? ids.filter((x) => x !== uid) : [...ids, uid];
    await patch({ member_ids: next });
  };

  const moveTo = async (listId) => { if (listId !== c.list_id) await patch({ list_id: listId }); };

  const setDue = async (val) => await patch({ due_date: val || null });

  const setColor = async (color) => {
    setShowColorPicker(false);
    if (color === c.color) return;
    try { await patch({ color }); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const submitShare = async () => {
    if (shareSelected.length === 0) return;
    setSharing(true);
    try {
      const r = await axios.post(
        `${API}/boards/${boardId}/cards/${c.id}/share`,
        { user_ids: shareSelected, role: shareRole, message: shareMessage || null },
        auth,
      );
      // Refresh card to pick up new member_ids/viewer_ids
      const fresh = await axios.get(`${API}/boards/${boardId}`, auth);
      const updated = (fresh.data.cards || []).find((x) => x.id === c.id);
      if (updated) setC(updated);
      onChange();
      setShowShareDialog(false);
      setShareSelected([]);
      setShareMessage('');
      const addedCount = r.data?.added || 0;
      const skipCount = r.data?.skipped || 0;
      alert(
        `Shared with ${addedCount} ${shareRole}${addedCount === 1 ? '' : 's'}.` +
        (skipCount > 0 ? ` ${skipCount} already had access.` : '')
      );
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to share');
    } finally {
      setSharing(false);
    }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      const r = await axios.post(`${API}/boards/${boardId}/cards/${c.id}/comments`, { text: newComment.trim() }, auth);
      setC((prev) => ({ ...prev, comments: [...(prev.comments || []), r.data] }));
      setNewComment('');
      onChange();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const toggleChecklistItem = async (idx) => {
    const items = (c.checklist || []).map((it, i) => i === idx ? { ...it, done: !it.done } : it);
    try {
      await axios.put(`${API}/boards/${boardId}/cards/${c.id}/checklist`, items, auth);
      setC((prev) => ({ ...prev, checklist: items }));
      onChange();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const addChecklistItem = async (text) => {
    if (!text.trim()) return;
    const items = [...(c.checklist || []), { text: text.trim(), done: false }];
    try {
      const r = await axios.put(`${API}/boards/${boardId}/cards/${c.id}/checklist`, items, auth);
      setC((prev) => ({ ...prev, checklist: r.data }));
      onChange();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const uploadFile = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await axios.post(`${API}/boards/${boardId}/cards/${c.id}/attachments`, fd, {
        headers: { ...auth.headers, 'Content-Type': 'multipart/form-data' },
      });
      setC((prev) => ({ ...prev, attachments: [...(prev.attachments || []), r.data] }));
      onChange();
    } catch (e) { alert(e.response?.data?.detail || 'Upload failed'); }
  };

  const removeAttachment = async (aid) => {
    try {
      await axios.delete(`${API}/boards/${boardId}/cards/${c.id}/attachments/${aid}`, auth);
      setC((prev) => ({ ...prev, attachments: (prev.attachments || []).filter((a) => a.id !== aid) }));
      onChange();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const deleteCard = async () => {
    if (!window.confirm('Delete this card?')) return;
    try {
      await axios.delete(`${API}/boards/${boardId}/cards/${c.id}`, auth);
      onClose(); onChange();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const summarizeCard = async () => {
    setSummarizing(true); setSummaryError(null); setSummary(null);
    try {
      const r = await axios.post(
        `${API}/boards/${boardId}/cards/${c.id}/summarize`,
        {},
        { ...auth, timeout: 30000 },
      );
      setSummary(r.data);
    } catch (e) {
      setSummaryError(e.response?.data?.detail || 'Failed to summarize');
    } finally {
      setSummarizing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: 'rgba(11,19,33,0.85)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="card-modal"
    >
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-3xl rounded-2xl my-8"
           style={{ background: '#1A2433', border: '1px solid #2D3748' }}>
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b" style={{ borderColor: '#2D3748' }}>
          <input
            value={c.title}
            disabled={!canEdit}
            onChange={(e) => setC({ ...c, title: e.target.value })}
            onBlur={saveTitle}
            className="text-lg font-semibold text-white bg-transparent flex-1 mr-4 outline-none border-b border-transparent focus:border-[#1E88E5] disabled:opacity-60"
            data-testid="card-title-input"
          />
          <button onClick={onClose} className="p-1 text-slate-400" data-testid="card-modal-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid md:grid-cols-[1fr_220px] gap-5 p-5">
          {/* MAIN */}
          <div className="space-y-5 min-w-0">
            {/* Labels */}
            {c.labels?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {c.labels.map((lab, i) => (
                  <span key={lab.id || `${lab.name}-${lab.color}-${i}`} className="px-2 py-0.5 rounded text-[11px] font-semibold text-white" style={{ background: lab.color }}>
                    {lab.name}
                  </span>
                ))}
              </div>
            )}

            {/* Move-to-list */}
            <div className="flex items-center gap-2 text-xs" style={{ color: '#A0AEC0' }}>
              <span>In list:</span>
              <select
                value={c.list_id}
                onChange={(e) => moveTo(e.target.value)}
                disabled={!canEdit}
                className="px-2 py-1 rounded text-xs text-white"
                style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                data-testid="card-list-select"
              >
                {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <span className="mx-1">•</span>
              <span>Due:</span>
              <input
                type="date"
                value={c.due_date || ''}
                onChange={(e) => setDue(e.target.value)}
                disabled={!canEdit}
                className="px-2 py-1 rounded text-xs text-white"
                style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                data-testid="card-due-input"
              />
            </div>

            {/* Description */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#64748B' }}>Description</p>
              <textarea
                rows={4}
                value={c.description || ''}
                disabled={!canEdit}
                onChange={(e) => setC({ ...c, description: e.target.value })}
                onBlur={saveDesc}
                placeholder="Add a more detailed description…"
                className="w-full p-3 rounded-lg text-sm text-white resize-y disabled:opacity-60"
                style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                data-testid="card-description"
              />
              {savingDesc && <p className="text-[10px] mt-1" style={{ color: '#64748B' }}>Saving…</p>}
            </div>

            {/* Checklist */}
            <div data-testid="card-checklist">
              <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#64748B' }}>Checklist</p>
              <ul className="space-y-1">
                {(c.checklist || []).map((it, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <input type="checkbox" checked={it.done} onChange={() => toggleChecklistItem(i)} disabled={!canEdit} />
                    <span className="text-sm" style={{ color: it.done ? '#64748B' : '#fff', textDecoration: it.done ? 'line-through' : 'none' }}>
                      {it.text}
                    </span>
                  </li>
                ))}
              </ul>
              {canEdit && (
                <ChecklistAdd onAdd={addChecklistItem} />
              )}
            </div>

            {/* Attachments */}
            <div data-testid="card-attachments">
              <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#64748B' }}>Attachments</p>

              {(() => {
                const all = c.attachments || [];
                const images = all.filter(isImageAttachment);
                const files = all.filter((a) => !isImageAttachment(a));
                const imgRefs = imageRefsFrom(all);
                return (
                  <>
                    {/* Image gallery — clickable thumbnails open lightbox */}
                    {images.length > 0 && (
                      <div data-testid="card-photo-gallery" className="mb-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-[11px] uppercase tracking-wider flex items-center gap-1" style={{ color: '#64748B' }}>
                            <ImageIcon className="w-3 h-3" /> Photos ({images.length})
                          </p>
                        </div>
                        <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
                          {images.map((a, idx) => {
                            const orphan = isLegacyOrphan?.(a);
                            return (
                            <div key={a.id} className="relative group">
                              <button
                                type="button"
                                onClick={() => { if (!orphan) openLightbox(imgRefs, idx); }}
                                className={`block w-full aspect-square rounded overflow-hidden ${orphan ? 'cursor-not-allowed' : 'cursor-zoom-in'}`}
                                style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                                data-testid={`card-photo-thumb-${a.id}`}
                                title={orphan ? `${a.original_name} — file was lost in a previous deployment. Please re-upload.` : a.original_name}
                                disabled={orphan}
                              >
                                {orphan ? (
                                  <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center" style={{ background: 'rgba(245,158,11,0.08)' }}>
                                    <ShieldAlert className="w-5 h-5 mb-1" style={{ color: '#FCD34D' }} />
                                    <span className="text-[9px] font-semibold leading-tight" style={{ color: '#FCD34D' }}>
                                      File lost — re-upload
                                    </span>
                                  </div>
                                ) : (
                                  <img
                                    src={attachmentHref(a)}
                                    alt={a.original_name}
                                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                                    loading="lazy"
                                  />
                                )}
                              </button>
                              {canEdit && (
                                <button
                                  onClick={() => removeAttachment(a.id)}
                                  className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  style={{ background: 'rgba(239,68,68,0.9)', color: 'white' }}
                                  data-testid={`remove-attachment-${a.id}`}
                                  title={`Remove ${a.original_name}`}
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Non-image files — type-coloured icon + download */}
                    {files.length > 0 && (
                      <ul className="space-y-1.5" data-testid="card-file-list">
                        {files.map((a) => {
                          const href = attachmentHref(a);
                          const meta = attachmentMeta(a);
                          const orphan = isLegacyOrphan?.(a);
                          const Icon = meta.icon === 'sheet'
                            ? FileSpreadsheet
                            : meta.icon === 'zip'
                              ? FileArchive
                              : FileText;
                          return (
                            <li
                              key={a.id}
                              className="p-2 rounded flex items-center gap-2"
                              style={{ background: '#0F1923', border: '1px solid #1F2D3F' }}
                              data-testid={`card-file-${a.id}`}
                            >
                              <span
                                className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0"
                                style={{ background: `${meta.color}1F`, border: `1px solid ${meta.color}55` }}
                                title={meta.label}
                              >
                                <Icon className="w-4 h-4" style={{ color: meta.color }} />
                              </span>
                              <div className="min-w-0 flex-1">
                                {orphan ? (
                                  <span
                                    className="text-sm truncate block line-through"
                                    style={{ color: '#94A3B8' }}
                                    title={a.original_name}
                                  >
                                    {a.original_name}
                                  </span>
                                ) : (
                                  <a
                                    href={href}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-sm text-white truncate block hover:underline"
                                    data-testid={`attachment-link-${a.id}`}
                                    title={a.original_name}
                                  >
                                    {a.original_name}
                                  </a>
                                )}
                                <div className="text-[10px] flex items-center gap-1.5 mt-0.5" style={{ color: '#64748B' }}>
                                  <span
                                    className="px-1.5 py-0.5 rounded text-[9px] font-semibold"
                                    style={{ color: meta.color, background: `${meta.color}14` }}
                                  >
                                    {meta.label}
                                  </span>
                                  {meta.archive && (
                                    <span
                                      className="px-1.5 py-0.5 rounded text-[9px] font-semibold inline-flex items-center gap-0.5"
                                      style={{ color: '#FCD34D', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)' }}
                                      title="Archive — please scan with your antivirus before opening. Hines Time does not unzip files on the server."
                                      data-testid={`attachment-archive-warning-${a.id}`}
                                    >
                                      <ShieldAlert className="w-2.5 h-2.5" />
                                      Scan before opening
                                    </span>
                                  )}
                                  {orphan && (
                                    <span
                                      className="px-1.5 py-0.5 rounded text-[9px] font-semibold inline-flex items-center gap-0.5"
                                      style={{ color: '#FCA5A5', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)' }}
                                      title="This file was uploaded before May 31, 2026 and was lost when production was redeployed. Please re-upload it."
                                      data-testid={`attachment-orphan-warning-${a.id}`}
                                    >
                                      <ShieldAlert className="w-2.5 h-2.5" />
                                      File lost — re-upload
                                    </span>
                                  )}
                                  <span>{fmtFileSize(a.size)}</span>
                                  {a.uploaded_by_name && (
                                    <span className="truncate" title={`Uploaded by ${a.uploaded_by_name}`}>
                                      · {a.uploaded_by_name}
                                    </span>
                                  )}
                                  {a.uploaded_at && (
                                    <time
                                      className="flex-shrink-0"
                                      dateTime={a.uploaded_at}
                                      title={fmtTimestampFull(a.uploaded_at)}
                                      data-testid={`attachment-timestamp-${a.id}`}
                                    >
                                      · {fmtTimestamp(a.uploaded_at)}
                                    </time>
                                  )}
                                </div>
                              </div>
                              <a
                                href={orphan ? '#' : href}
                                download={a.original_name}
                                target="_blank"
                                rel="noreferrer"
                                onClick={orphan ? (e) => e.preventDefault() : undefined}
                                className={`text-xs p-1.5 rounded flex-shrink-0 ${orphan ? 'opacity-30 cursor-not-allowed' : 'hover:bg-white/5'}`}
                                style={{ color: '#94A3B8' }}
                                title={orphan ? 'File no longer available — please re-upload' : 'Download'}
                                data-testid={`attachment-download-${a.id}`}
                              >
                                <Download className="w-3.5 h-3.5" />
                              </a>
                              {canEdit && (
                                <button
                                  onClick={() => removeAttachment(a.id)}
                                  className="text-xs p-1.5 rounded hover:bg-red-500/10 flex-shrink-0"
                                  style={{ color: '#EF4444' }}
                                  data-testid={`remove-attachment-${a.id}`}
                                  title="Remove"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}

                    {all.length === 0 && (
                      <p className="text-xs italic" style={{ color: '#64748B' }}>No attachments yet.</p>
                    )}
                  </>
                );
              })()}

              {canEdit && (
                <label
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium cursor-pointer"
                  style={{ background: '#0F1923', color: '#A0AEC0', border: '1px solid #2D3748' }}
                  data-testid="card-attach-btn"
                  title="Upload images, PDFs, Word/Excel/PowerPoint, OpenDocument, CSV, TXT, or ZIP — up to 25 MB"
                >
                  <Paperclip className="w-3.5 h-3.5" /> Upload file
                  <input
                    type="file"
                    hidden
                    accept={[
                      'image/*',
                      'application/pdf',
                      '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
                      '.odt', '.ods', '.odp', '.csv', '.txt', '.zip',
                      // Explicit MIMEs for browsers that don't honor extensions
                      'application/msword',
                      'application/vnd.ms-excel',
                      'application/vnd.ms-powerpoint',
                      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                      'application/vnd.oasis.opendocument.text',
                      'application/vnd.oasis.opendocument.spreadsheet',
                      'application/vnd.oasis.opendocument.presentation',
                      'text/csv',
                      'text/plain',
                      'application/zip',
                    ].join(',')}
                    onChange={(e) => { if (e.target.files?.[0]) uploadFile(e.target.files[0]); e.target.value = ''; }}
                    data-testid="card-attach-input"
                  />
                </label>
              )}
            </div>

            {/* AI Summary */}
            <div data-testid="card-ai-summary">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5" style={{ color: '#64748B' }}>
                  <Sparkles className="w-3 h-3" /> AI Summary
                </p>
                <button
                  onClick={summarizeCard}
                  disabled={summarizing || ((c.comments || []).length === 0 && (c.activity || []).length === 0)}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold disabled:opacity-50"
                  style={{ background: 'rgba(167,139,250,0.15)', color: '#A78BFA', border: '1px solid rgba(167,139,250,0.35)' }}
                  data-testid="summarize-card-btn"
                  title="Generate a 2-3 sentence summary of comments + activity"
                >
                  <Sparkles className="w-3 h-3" />
                  {summarizing ? 'Thinking…' : (summary ? 'Re-summarize' : 'Summarize')}
                </button>
              </div>
              {summaryError && (
                <p className="text-xs p-2 rounded" style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444' }} data-testid="summary-error">
                  {summaryError}
                </p>
              )}
              {summary && (
                <div className="p-3 rounded-lg text-sm" style={{ background: 'linear-gradient(135deg, rgba(167,139,250,0.08), rgba(30,136,229,0.06))', border: '1px solid rgba(167,139,250,0.25)', color: '#E2E8F0' }} data-testid="card-summary-text">
                  {summary.summary}
                  <p className="text-[10px] mt-2" style={{ color: '#64748B' }}>
                    {summary.model} · based on {summary.comments_included} comments + {summary.activity_included} activity entries
                  </p>
                </div>
              )}
            </div>

            {/* Comments */}
            <div data-testid="card-comments">
              <p className="text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: '#64748B' }}>
                <MessageSquare className="w-3 h-3" /> Comments & Activity
              </p>
              <div className="flex gap-2 mb-3">
                <input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addComment(); }}
                  placeholder="Write a comment…"
                  className="flex-1 px-3 py-2 rounded-lg text-sm text-white"
                  style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                  data-testid="comment-input"
                />
                <button onClick={addComment} className="px-3 py-2 rounded-lg text-white" style={{ background: '#1E88E5' }} data-testid="comment-send">
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <ul className="space-y-2">
                {(c.comments || []).slice().reverse().map((cm) => (
                  <li key={cm.id} className="p-2.5 rounded-lg" style={{ background: '#0F1923' }} data-testid={`comment-item-${cm.id}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-xs font-semibold text-white">{cm.user_name}</p>
                      {cm.created_at && (
                        <time
                          className="text-[10px] flex-shrink-0"
                          style={{ color: '#64748B' }}
                          dateTime={cm.created_at}
                          title={fmtTimestampFull(cm.created_at)}
                          data-testid={`comment-timestamp-${cm.id}`}
                        >
                          {fmtTimestamp(cm.created_at)}
                        </time>
                      )}
                    </div>
                    <p className="text-sm mt-0.5" style={{ color: '#CBD5E1' }}>{cm.text}</p>
                  </li>
                ))}
                {(c.activity || []).slice().reverse().slice(0, 8).map((a) => (
                  <li key={a.id} className="text-xs flex items-baseline justify-between gap-2" style={{ color: '#64748B' }} data-testid={`activity-item-${a.id}`}>
                    <span className="truncate">{a.user_name} {a.text}</span>
                    {a.created_at && (
                      <time
                        className="text-[10px] flex-shrink-0"
                        style={{ color: '#475569' }}
                        dateTime={a.created_at}
                        title={fmtTimestampFull(a.created_at)}
                        data-testid={`activity-timestamp-${a.id}`}
                      >
                        {fmtTimestamp(a.created_at)}
                      </time>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* SIDE PANEL */}
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>Add to card</p>

            {/* Share button — prominent */}
            <button
              onClick={() => setShowShareDialog(true)}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium"
              style={{ background: 'linear-gradient(135deg, #1E88E5, #00C896)', color: 'white' }}
              data-testid="share-card-btn"
            >
              <Share2 className="w-4 h-4" /> Share card
            </button>

            {/* Shared with — combined view of members (editors) + viewers */}
            <details className="rounded-lg" style={{ background: '#0F1923', border: '1px solid #2D3748' }}>
              <summary className="px-3 py-2 text-sm text-white flex items-center gap-2 cursor-pointer">
                <UsersIcon className="w-4 h-4" /> Shared with
                {((c.member_ids?.length || 0) + (c.viewer_ids?.length || 0)) > 0 && (
                  <span className="text-xs" style={{ color: '#64748B' }}>
                    {(c.member_ids?.length || 0) + (c.viewer_ids?.length || 0)}
                  </span>
                )}
              </summary>
              <ul className="max-h-48 overflow-y-auto px-2 pb-2 space-y-1" data-testid="card-member-list">
                {users.map((u) => {
                  const isEditor = (c.member_ids || []).includes(u.id);
                  const isViewer = (c.viewer_ids || []).includes(u.id);
                  return (
                    <li key={u.id}>
                      <button
                        onClick={() => toggleMember(u.id)}
                        className="w-full flex items-center gap-2 px-2 py-1 rounded text-xs text-left hover:bg-white/5"
                        data-testid={`card-member-toggle-${u.id}`}
                      >
                        <input type="checkbox" readOnly checked={isEditor} />
                        <span className="text-white flex-1">{u.name}</span>
                        {isEditor && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(30,136,229,0.2)', color: '#1E88E5' }}>Editor</span>
                        )}
                        {isViewer && !isEditor && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(167,139,250,0.2)', color: '#A78BFA' }}>Viewer</span>
                        )}
                        <span className="text-[10px]" style={{ color: '#64748B' }}>{u.role}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </details>

            {/* Color */}
            <details
              className="rounded-lg"
              style={{ background: '#0F1923', border: '1px solid #2D3748' }}
              open
              onToggle={(e) => setShowColorPicker(e.currentTarget.open)}
            >
              <summary className="px-3 py-2 text-sm text-white flex items-center gap-2 cursor-pointer">
                <Palette className="w-4 h-4" /> Card color
                {c.color && (
                  <span className="w-3 h-3 rounded ml-auto" style={{ background: c.color }} />
                )}
              </summary>
              <div className="px-2 pb-2" data-testid="card-color-picker">
                {/* 7-column swatch grid — fits 21 presets in 3 rows */}
                <div className="grid grid-cols-7 gap-1.5">
                  {CARD_COLORS.map((opt) => {
                    const selected = (c.color || null) === opt.color;
                    return (
                      <button
                        key={opt.name}
                        onClick={() => setColor(opt.color)}
                        disabled={!canEdit}
                        className="relative w-7 h-7 rounded transition-transform hover:scale-110 disabled:opacity-60 disabled:hover:scale-100"
                        style={{
                          background: opt.color || 'transparent',
                          border: opt.color
                            ? (selected ? '2px solid #fff' : '1px solid rgba(255,255,255,0.15)')
                            : '1px dashed #64748B',
                        }}
                        data-testid={`card-color-${opt.name.toLowerCase().replace(' ', '-')}`}
                        title={opt.name}
                      >
                        {!opt.color && (
                          <X className="w-3 h-3 mx-auto" style={{ color: '#64748B' }} />
                        )}
                        {selected && opt.color && (
                          <Check
                            className="w-3.5 h-3.5 mx-auto"
                            style={{ color: '#fff', filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.5))' }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Custom hex picker — native input for any color outside the palette */}
                <div className="mt-2 pt-2 border-t flex items-center gap-2" style={{ borderColor: '#1F2D3F' }}>
                  <span className="text-[11px]" style={{ color: '#94A3B8' }}>Custom:</span>
                  <input
                    type="color"
                    value={c.color || '#1E88E5'}
                    disabled={!canEdit}
                    onChange={(e) => setC({ ...c, color: e.target.value })}
                    onBlur={(e) => setColor(e.target.value)}
                    className="w-8 h-7 rounded cursor-pointer disabled:cursor-not-allowed bg-transparent"
                    style={{ border: '1px solid #2D3748' }}
                    data-testid="card-color-custom"
                    title="Pick any color"
                  />
                  <input
                    type="text"
                    value={c.color || ''}
                    disabled={!canEdit}
                    onChange={(e) => setC({ ...c, color: e.target.value })}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      // Validate hex shape before pushing to backend
                      if (!v) { setColor(null); return; }
                      if (/^#[0-9A-Fa-f]{6}$/.test(v) || /^#[0-9A-Fa-f]{3}$/.test(v)) {
                        setColor(v);
                      } else {
                        // Revert on invalid input
                        setC({ ...c, color: card.color || null });
                      }
                    }}
                    placeholder="#RRGGBB"
                    className="flex-1 px-2 py-1 rounded text-[11px] text-white font-mono"
                    style={{ background: '#0B1321', border: '1px solid #2D3748' }}
                    data-testid="card-color-hex"
                  />
                </div>
              </div>
            </details>

            {/* Labels — Trello-style picker: search, create, edit, apply */}
            <BoardLabelsPicker
              card={c}
              board={board}
              auth={auth}
              boardId={boardId}
              canEdit={canEdit}
              canAdminBoard={canAdminBoard}
              onCardLabelsChange={(next) => patch({ labels: next })}
              onBoardLabelsChange={onChange}
            />

            {canAdminBoard && (
              <button
                onClick={deleteCard}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium"
                style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.3)' }}
                data-testid="delete-card-btn"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete card
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Share dialog */}
      {showShareDialog && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.7)' }}
          onClick={() => setShowShareDialog(false)}
          data-testid="share-dialog-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-xl p-5"
            style={{ background: '#0F1923', border: '1px solid #2D3748' }}
            data-testid="share-dialog"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Share2 className="w-5 h-5" style={{ color: '#1E88E5' }} />
                Share "{c.title}"
              </h3>
              <button onClick={() => setShowShareDialog(false)} className="p-1 rounded hover:bg-white/5" data-testid="share-dialog-close">
                <X className="w-4 h-4" style={{ color: '#64748B' }} />
              </button>
            </div>

            {/* Role selector */}
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => setShareRole('editor')}
                className="flex-1 px-3 py-2 rounded text-xs font-medium"
                style={{
                  background: shareRole === 'editor' ? 'rgba(30,136,229,0.2)' : '#1A2433',
                  border: `1px solid ${shareRole === 'editor' ? '#1E88E5' : '#2D3748'}`,
                  color: shareRole === 'editor' ? '#1E88E5' : '#94A3B8',
                }}
                data-testid="share-role-editor"
              >
                Editor (full access)
              </button>
              <button
                onClick={() => setShareRole('viewer')}
                className="flex-1 px-3 py-2 rounded text-xs font-medium"
                style={{
                  background: shareRole === 'viewer' ? 'rgba(167,139,250,0.2)' : '#1A2433',
                  border: `1px solid ${shareRole === 'viewer' ? '#A78BFA' : '#2D3748'}`,
                  color: shareRole === 'viewer' ? '#A78BFA' : '#94A3B8',
                }}
                data-testid="share-role-viewer"
              >
                <Eye className="w-3 h-3 inline mr-1" /> Viewer (read + comment)
              </button>
            </div>

            {/* User picker */}
            <p className="text-xs uppercase tracking-wider mb-1.5" style={{ color: '#64748B' }}>People in your company</p>
            <ul className="max-h-56 overflow-y-auto rounded mb-3" style={{ background: '#1A2433', border: '1px solid #2D3748' }} data-testid="share-user-list">
              {users.filter((u) => u.id !== currentUserId).map((u) => {
                const checked = shareSelected.includes(u.id);
                const alreadyEditor = (c.member_ids || []).includes(u.id);
                const alreadyViewer = (c.viewer_ids || []).includes(u.id);
                return (
                  <li key={u.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setShareSelected((prev) => prev.includes(u.id)
                          ? prev.filter((x) => x !== u.id)
                          : [...prev, u.id]);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-white/5"
                      data-testid={`share-pick-${u.id}`}
                    >
                      <input type="checkbox" readOnly checked={checked} />
                      <span className="text-white flex-1 truncate">{u.name}</span>
                      <span className="text-[10px]" style={{ color: '#64748B' }}>{u.role}</span>
                      {alreadyEditor && shareRole === 'editor' && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,200,150,0.15)', color: '#00C896' }}>has access</span>
                      )}
                      {alreadyViewer && shareRole === 'viewer' && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,200,150,0.15)', color: '#00C896' }}>has access</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Optional message */}
            <textarea
              value={shareMessage}
              onChange={(e) => setShareMessage(e.target.value)}
              placeholder="Optional message (sent in the email + notification)…"
              rows={2}
              maxLength={500}
              className="w-full px-3 py-2 rounded text-sm text-white resize-none mb-3"
              style={{ background: '#1A2433', border: '1px solid #2D3748' }}
              data-testid="share-message-input"
            />

            <div className="flex gap-2">
              <button
                onClick={() => setShowShareDialog(false)}
                className="flex-1 px-3 py-2 rounded text-sm"
                style={{ background: '#1A2433', color: '#94A3B8', border: '1px solid #2D3748' }}
                data-testid="share-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={submitShare}
                disabled={sharing || shareSelected.length === 0}
                className="flex-1 px-3 py-2 rounded text-sm font-medium disabled:opacity-50"
                style={{ background: '#1E88E5', color: 'white' }}
                data-testid="share-submit-btn"
              >
                {sharing ? 'Sharing…' : `Share with ${shareSelected.length || 0}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ChecklistAdd({ onAdd }) {
  const [val, setVal] = useState('');
  return (
    <div className="mt-2 flex gap-2">
      <input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { onAdd(val); setVal(''); } }}
        placeholder="Add an item…"
        className="flex-1 px-2 py-1 rounded text-xs text-white"
        style={{ background: '#0F1923', border: '1px solid #2D3748' }}
        data-testid="checklist-add-input"
      />
      <button
        onClick={() => { onAdd(val); setVal(''); }}
        className="px-2 py-1 rounded text-xs"
        style={{ background: '#1E88E5', color: '#fff' }}
        data-testid="checklist-add-btn"
      >
        Add
      </button>
    </div>
  );
}
